import { useState, useMemo, useEffect } from 'react';

/**
 * Hook for paginated options
 * @param {Object} options - The options to paginate
 * @param {string} searchValue - The search value to filter the options
 * @param {number} batchSize - The batch size to load more options
 * @param {boolean} disableFilter - Skip local filtering (options already filtered server-side)
 * @returns {Object}
 * @property {Array} filteredOptions - The filtered options based on the search value
 * @property {Array} visibleOptions - The visible options based on the batch size
 * @property {Function} onLoadMore - The function to load more options
 * @property {Boolean} hasMore - Whether there are more options to load
 */

const usePaginatedOptions = ({
    options = [],
    searchValue = '',
    batchSize = 20,
    disableFilter = false,
}) => {
    const [visibleOptions, setVisibleOptions] = useState([]);
    const [hasMore, setHasMore] = useState(false);

    const filteredOptions = useMemo(() => {
        // Server-driven options (e.g. geocode results) are already filtered for the
        // query. Re-filtering by the typed text drops valid results whose normalized
        // label doesn't contain the literal tokens (e.g. "Street" vs "St").
        if (disableFilter) return options;

        const keywords = searchValue.toLowerCase().split(/\s+/).filter(Boolean);

        return options.filter((option) => {
            const label = option.label.toLowerCase();
            return keywords.every((keyword) => label.includes(keyword));
        });
    }, [options, searchValue, disableFilter]);

    // Reset visible options when search value or options change
    useEffect(() => {
        const newVisibleOptions = filteredOptions.slice(0, batchSize);
        const newHasMore = filteredOptions.length > batchSize;

        setVisibleOptions(newVisibleOptions);
        setHasMore(newHasMore);
    }, [filteredOptions, batchSize]);

    const onLoadMore = () => {
        setVisibleOptions((prev) => {
            const newVisibleOptions = [
                ...prev,
                ...filteredOptions.slice(prev.length, prev.length + batchSize),
            ];
            const newHasMore = newVisibleOptions.length < filteredOptions.length;
            // Update hasMore based on whether there are more options to load
            setHasMore(newHasMore);
            return newVisibleOptions;
        });
    };

    return {
        filteredOptions,
        visibleOptions,
        onLoadMore,
        hasMore,
    };
};

export default usePaginatedOptions;
